import { useTranslation } from "@sagot/i18n";
import { Button, Icon, toast, type ButtonProps } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import type { useConversationActions } from "./inbox-api";
import type { ConversationItem } from "./inbox-model";
import { snoozeEnd } from "./snooze";

interface UnsnoozeButtonProps {
  conversation: Pick<ConversationItem, "snoozed_until">;
  actions: Pick<ReturnType<typeof useConversationActions>, "unsnooze">;
  /** The member may reply in this conversation (`inbox.reply`). */
  canReply: boolean;
  now: number;
  variant?: ButtonProps["variant"];
  testId?: string;
}

/** "Unsnooze": wakes a snoozed conversation now. Renders nothing for a member who cannot reply or a conversation that is awake. */
export function UnsnoozeButton({ conversation, actions, canReply, now, variant = "secondary", testId = "unsnooze" }: UnsnoozeButtonProps) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  if (!canReply || !snoozeEnd(conversation, now)) return null;
  const onClick = () => {
    actions.unsnooze.mutateAsync().then(
      () => toast.success(t("engage.inbox.snooze.woken")),
      (error: unknown) => toast.error(errorMessage(error)),
    );
  };
  return (
    <Button size="sm" variant={variant} loading={actions.unsnooze.isPending} onClick={onClick} data-testid={testId}>
      <Icon name="bell" />
      {t("engage.inbox.snooze.unsnooze")}
    </Button>
  );
}
